/* @flow */
import invariant from 'invariant';
import type { MessageType } from 'chat/lib/MessageInterfacePropType';

// TODO this should really be polymorphic
export type MessageBunch = {
  date: Date,
  senderId?: ?Backbone$Id,
  messageType: string,
  // $FlowFixMe other types incompatible with ChatSystemMessage
  messages: MessageType[],
};

export const sameDay = (a: Date, b: ?Date): boolean =>
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function makeBunch(message: MessageType): MessageBunch {
  invariant(typeof message.getSentAt === 'function', 'makeBunch message must support getSentAt');
  const date = message.getSentAt();
  invariant(typeof message.getSender === 'function', 'makeBunch message must support getSender');
  const sender = message.getSender();
  return {
    date,
    senderId: sender ? sender.id : undefined,
    messageType: message.getType(),
    messages: [message],
  };
}

const MESSAGE_BUNCH_TIME_WINDOW = 1000 * 60 * 5; // 5 min

/**
 * A bunch is of the same type, on same day, from same sender, within MESSAGE_BUNCH_TIME_WINDOW.
 */
function messageFits(message: MessageType, bunch: MessageBunch): boolean {
  if (message.getType() !== bunch.messageType) {
    return false;
  }
  if (typeof message.getSentAt === 'function' && !sameDay(message.getSentAt(), bunch.date)) {
    return false;
  }
  const sender = typeof message.getSender === 'function' && message.getSender();
  if (sender && sender.id !== bunch.senderId) {
    return false;
  }
  if (
    message.getType() !== 'system' &&
    typeof message.getSentAt === 'function' &&
    message.getSentAt() - bunch.date > MESSAGE_BUNCH_TIME_WINDOW
  ) {
    return false;
  }
  return true;
}

function bunchMessages(messages: MessageType[]): MessageBunch[] {
  if (messages.length === 0) {
    return [];
  }

  const messageStack = Array.from(messages).reverse();

  const bunches = [makeBunch(messageStack.pop())];

  while (messageStack.length > 0) {
    const currBunch = bunches[bunches.length - 1];
    const currMessage = messageStack.pop();

    if (messageFits(currMessage, currBunch)) {
      currBunch.messages.push(currMessage);
    } else {
      bunches.push(makeBunch(currMessage));
    }
  }

  return bunches;
}

export function extractGapAndBunches(messages: MessageType[]) {
  const gapMessage = messages.length > 0 && messages[0].getType() === 'gap' ? messages[0] : null;
  const fullMessages = gapMessage ? messages.slice(1) : messages;
  const bunches = bunchMessages(fullMessages);
  return { gapMessage, bunches };
}

// XXX so many types to handle
export type MessageListItems = Array<MessageType | MessageBunch | Date>;

export function extractListItems(messages: MessageType[]): MessageListItems {
  const { gapMessage, bunches } = extractGapAndBunches(messages);

  const items = [];

  if (gapMessage) {
    // no date to render before gap because we don't know what date the message
    // represented by the gap was on until we fetch it.
    items.push(gapMessage);
  }

  let bunchDate = null;
  for (const bunch: MessageBunch of bunches) {
    if (!sameDay(bunch.date, bunchDate)) {
      bunchDate = bunch.date;
      items.push(bunchDate);
    }
    items.push(bunch);
  }

  return items;
}
